import React, { useState } from 'react';
import {
  Box,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Paper
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

/**
 * ChipListEditor
 * Component reusable per visualitzar i editar una llista de cadenes (tags / chips)
 */
const ChipListEditor = ({
  items = [],
  onChange,
  placeholder = 'Afegir element...',
  label,
  helperText,
  color = 'default',
  disabled = false,
  isMonospace = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  // Assegurar que items és un Array
  const safeItems = Array.isArray(items)
    ? items
    : (typeof items === 'string' && items.trim() ? [items.trim()] : []);

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    if (safeItems.includes(trimmed)) {
      setError('Aquest valor ja existeix a la llista');
      return;
    }

    onChange([...safeItems, trimmed]);
    setInputValue('');
    setError('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleDelete = (indexToDelete) => {
    const updated = safeItems.filter((_, idx) => idx !== indexToDelete);
    onChange(updated);
  };

  return (
    <Box sx={{ my: 1.5 }}>
      {label && (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
          {label} ({safeItems.length})
        </Typography>
      )}

      {helperText && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          {helperText}
        </Typography>
      )}

      {/* Lista de Chips */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          minHeight: 48,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.8,
          alignItems: 'center',
          backgroundColor: disabled ? '#f5f5f5' : '#fafafa',
          borderRadius: 2,
          mb: 1
        }}
      >
        {safeItems.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
            Sense elements definits
          </Typography>
        ) : (
          safeItems.map((item, idx) => (
            <Chip
              key={`${item}-${idx}`}
              label={item}
              color={color}
              size="small"
              onDelete={disabled ? undefined : () => handleDelete(idx)}
              sx={{
                fontSize: isMonospace ? '0.78rem' : '0.8125rem',
                fontFamily: isMonospace ? 'monospace' : 'inherit',
                maxWidth: '100%',
                fontWeight: 500,
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }
              }}
            />
          ))
        )}
      </Paper>

      {/* Input para añadir nuevo elemento */}
      {!disabled && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={handleKeyDown}
            error={Boolean(error)}
            helperText={error}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: isMonospace ? 'monospace' : 'inherit',
                fontSize: '0.875rem'
              }
            }}
          />
          <Tooltip title="Afegir a la llista">
            <span>
              <IconButton
                color="primary"
                onClick={handleAdd}
                disabled={!inputValue.trim()}
                sx={{ p: 1 }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default ChipListEditor;
